<script setup lang="ts">
import { forgotPassword } from '@/api/auth'
import { ApiError } from '@/api/client'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

definePage({
  meta: { layout: 'blank', public: true, publicOnly: true },
})

const router = useRouter()

const email = ref('')
const loading = ref(false)
const error = ref('')
const success = ref('')

const submit = async () => {
  error.value = ''
  success.value = ''
  if (!email.value.trim()) {
    error.value = 'Enter your email.'

    return
  }

  loading.value = true
  try {
    const res = await forgotPassword(email.value.trim())

    sessionStorage.setItem('bt_reset_email', email.value.trim())
    success.value = res.message

    // Un respiro para que se lea la confirmación antes de saltar al paso 2.
    setTimeout(() => router.push({ name: 'reset-password' }), 1200)
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not send reset code.'
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-md-none d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper"
    no-gutters
  >
    <AuthIllustration />

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            Forgot Password? 🔒
          </h4>
          <p class="mb-0">
            We'll send a 4-digit code to reset your password.
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="submit">
            <VRow>
              <VCol
                v-if="error"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                  density="compact"
                >
                  {{ error }}
                </VAlert>
              </VCol>

              <VCol
                v-if="success"
                cols="12"
              >
                <VAlert
                  type="success"
                  variant="tonal"
                  density="compact"
                >
                  {{ success }}
                </VAlert>
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="email"
                  autofocus
                  label="Email"
                  type="email"
                  autocomplete="email"
                  placeholder="you@divecenter.com"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                  :loading="loading"
                >
                  Send reset code
                </VBtn>
              </VCol>

              <VCol cols="12">
                <RouterLink
                  class="d-flex align-center justify-center"
                  :to="{ name: 'login' }"
                >
                  <VIcon
                    icon="ri-arrow-left-s-line"
                    class="flip-in-rtl"
                  />
                  <span>Back to login</span>
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
